import React, { useState } from 'react';
import { Card, Empty, Button, Space, Tooltip, Popconfirm, message, Upload, Modal, Spin, Tag } from 'antd';
import { 
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  DownloadOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  EyeOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { dealsApi, fetchAuthenticatedBlob } from '../../../utils/api';
import { getApiUrl } from '../../../config/api';
import { getUserUploadsUrl } from '../../../utils/userUploadsUrl';
import { canDeleteLeadRecord } from '../../../utils/leadRecordPermissions';

const FilesTab = ({ dealId, data = [], filters, permissions, onRefresh }) => {
  const [files, setFiles] = useState(data?.files || []);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  React.useEffect(() => {
    setFiles(data?.files || []);
  }, [data]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // تشخیص نوع فایل از نام یا پسوند
  const getFileExtension = (file) => {
    const filename = file.filename || file.name || file.hashname || '';
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ext;
  };

  const isImageFile = (file) => {
    const ext = getFileExtension(file);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  };

  const isPdfFile = (file) => {
    const ext = getFileExtension(file);
    return ext === 'pdf';
  };

  const isVideoFile = (file) => {
    const ext = getFileExtension(file);
    return ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext);
  };

  const isAudioFile = (file) => {
    const ext = getFileExtension(file);
    return ['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(ext);
  };

  const isPreviewable = (file) => {
    return isImageFile(file) || isPdfFile(file) || isVideoFile(file) || isAudioFile(file);
  };

  // آیکون مناسب برای هر نوع فایل
  const getFileIcon = (file) => {
    const ext = getFileExtension(file);
    if (isImageFile(file)) return <FileImageOutlined className="text-lg" style={{ color: '#52c41a' }} />;
    if (isPdfFile(file)) return <FilePdfOutlined className="text-lg" style={{ color: '#ff4d4f' }} />;
    if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="text-lg" style={{ color: '#1890ff' }} />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="text-lg" style={{ color: '#52c41a' }} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FileZipOutlined className="text-lg" style={{ color: '#faad14' }} />;
    return <FileOutlined className="text-lg" style={{ color: '#1890ff' }} />;
  };

  // ساخت URL مستقیم فایل (ترجیح با file_url عمومی از API)
  const getFileUrl = (file) => {
    if (file.file_url) return file.file_url;
    if (file.url) return file.url;
    if (file.download_url) return file.download_url;
    if (file.external_link) return file.external_link;
    if (file.hashname) {
      const ownerId = file.deal_id || dealId;
      // بک‌اند فایل‌های معامله را زیر lead-files ذخیره می‌کند
      return getUserUploadsUrl(
        `/user-uploads/lead-files/${ownerId}/${file.hashname}`,
      );
    }
    return null;
  };

  const loadPreviewBlobUrl = async (file) => {
    const url = getApiUrl(`deal-files/download/${file.id}`);
    const blob = await fetchAuthenticatedBlob(url);
    return window.URL.createObjectURL(blob);
  };

  const downloadFromDirectUrl = async (fileUrl, filename) => {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`خطا در دریافت فایل (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setPreviewVisible(true);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      const directUrl = getFileUrl(file);

      // اولویت با URL عمومی — پیش‌نمایش تصویر/PDF بدون نیاز به download API
      if (directUrl && isPreviewable(file)) {
        setPreviewUrl(directUrl);
        return;
      }

      if (isPreviewable(file) && file.id) {
        const blobUrl = await loadPreviewBlobUrl(file);
        setPreviewUrl(blobUrl);
        return;
      }

      if (directUrl) {
        setPreviewUrl(directUrl);
        return;
      }

      message.warning('پیش‌نمایش برای این فایل در دسترس نیست');
      setPreviewVisible(false);
    } catch (error) {
      console.error('Error loading preview:', error);
      message.error('خطا در بارگذاری پیش‌نمایش');
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    // آزاد کردن Blob URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewVisible(false);
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const renderPreviewContent = () => {
    if (previewLoading) {
      return (
        <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
          <Spin size="large" tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری...</span>} />
        </div>
      );
    }

    if (!previewUrl || !previewFile) return null;

    if (isImageFile(previewFile)) {
      return (
        <div className="flex items-center justify-center" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <img
            src={previewUrl}
            alt={previewFile.filename || previewFile.name}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
            onError={() => {
              message.error('خطا در بارگذاری تصویر');
            }}
          />
        </div>
      );
    }

    if (isPdfFile(previewFile)) {
      return (
        <iframe
          src={previewUrl}
          title={previewFile.filename || previewFile.name}
          style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }}
        />
      );
    }

    if (isVideoFile(previewFile)) {
      return (
        <div className="flex items-center justify-center">
          <video
            src={previewUrl}
            controls
            style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }}
          >
            مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
          </video>
        </div>
      );
    }

    if (isAudioFile(previewFile)) {
      return (
        <div className="flex items-center justify-center py-12">
          <audio src={previewUrl} controls style={{ width: '100%' }}>
            مرورگر شما از پخش صوتی پشتیبانی نمی‌کند.
          </audio>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <FileOutlined style={{ fontSize: 64, color: '#ccc' }} />
        <p className="mt-4 text-gray-500" style={{ fontFamily: 'CustomFont' }}>
          پیش‌نمایش برای این نوع فایل پشتیبانی نمی‌شود.
        </p>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(previewFile)}
          className="mt-2 bg-blue-600 hover:bg-blue-700 border-0"
          style={{ fontFamily: 'CustomFont' }}
        >
          دانلود فایل
        </Button>
      </div>
    );
  };

  const handleDownload = async (file) => {
    const filename = file.filename || file.name || file.hashname || 'deal-file';
    const directUrl = getFileUrl(file);

    try {
      if (directUrl) {
        try {
          await downloadFromDirectUrl(directUrl, filename);
        } catch {
          // اگر CORS مانع fetch شد، لینک عمومی را در تب جدید باز کن
          window.open(directUrl, '_blank', 'noopener,noreferrer');
        }
        message.success('فایل با موفقیت دانلود شد');
        return;
      }

      await dealsApi.downloadFile(file.id, filename);
      message.success('فایل با موفقیت دانلود شد');
    } catch (error) {
      console.error('Error downloading file:', error);
      message.error('خطا در دانلود فایل');
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await dealsApi.deleteFile(fileId);
      message.success('فایل با موفقیت حذف شد');
      onRefresh();
    } catch (error) {
      console.error('Error deleting file:', error);
      message.error('خطا در حذف فایل');
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('لطفاً حداقل یک فایل انتخاب کنید');
      return;
    }

    setUploading(true);
    try {
      const filesToUpload = fileList.map(item => item.originFileObj || item);
      await dealsApi.uploadFile(parseInt(dealId), filesToUpload);
      message.success('فایل‌ها با موفقیت آپلود شدند');
      setFileList([]);
      onRefresh();
    } catch (error) {
      console.error('Error uploading files:', error);
      message.error('خطا در آپلود فایل‌ها');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    beforeUpload: () => false, // جلوگیری از آپلود خودکار
    multiple: true,
  };

  const renderUploadDropArea = (emptyState = false) => (
    <Upload.Dragger
      {...uploadProps}
      showUploadList={false}
      openFileDialogOnClick
      className={emptyState ? 'bg-white dark:bg-gray-900/20' : ''}
      style={emptyState ? { borderRadius: 12 } : undefined}
    >
      <div className="flex flex-col items-center justify-center gap-2 py-2">
        <UploadOutlined className="text-blue-600 dark:text-blue-400 text-lg" />
        <span className="text-sm text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
          {fileList.length > 0 ? `${fileList.length} فایل انتخاب شده` : 'انتخاب یا رها کردن فایل'}
        </span>
        {emptyState ? (
          <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
            هنوز فایلی ثبت نشده؛ فایل را همین‌جا رها کنید
          </span>
        ) : null}
      </div>
    </Upload.Dragger>
  );

  return (
    <div className="space-y-4">
      {/* Header with Upload */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[230px]">
            {renderUploadDropArea()}
          </div>
          {fileList.length > 0 && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleUpload}
              loading={uploading}
              style={{ fontFamily: 'CustomFont' }}
              className="bg-green-600 hover:bg-green-700 border-0"
            >
              آپلود ({fileList.length})
            </Button>
          )}
        </div>
      </div>
      {fileList.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {fileList.map((f) => (
            <Tag key={f.uid} closable onClose={() => setFileList((prev) => prev.filter((x) => x.uid !== f.uid))}>
              {f.name}
            </Tag>
          ))}
        </div>
      )}

      {/* Files List */}
      {!files || files.length === 0 ? (
        <div className="py-8">
          {renderUploadDropArea(true)}
          <div className="mt-4">
            <Empty
              description="هیچ فایلی ثبت نشده است"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {files.map((file) => (
            <Card
              key={file.id}
              className="shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700"
              style={{ fontFamily: 'CustomFont' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                    {getFileIcon(file)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 m-0 mb-1 truncate" style={{ fontFamily: 'CustomFont' }}>
                      {file.filename || file.name || 'فایل بدون نام'}
                    </h3>
                    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                      {file.size && (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          حجم: {formatFileSize(parseInt(file.size))}
                        </span>
                      )}
                      {file.created_at && (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          {moment(file.created_at).format('jYYYY/jMM/jDD HH:mm')}
                        </span>
                      )}
                      {getFileExtension(file) && (
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {getFileExtension(file).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <Space className="flex-shrink-0">
                  {isPreviewable(file) && (
                    <Tooltip title="پیش‌نمایش">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                        style={{ fontFamily: 'CustomFont' }}
                        onClick={() => handlePreview(file)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="دانلود">
                    <Button
                      type="text"
                      icon={<DownloadOutlined />}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      style={{ fontFamily: 'CustomFont' }}
                      onClick={() => handleDownload(file)}
                    />
                  </Tooltip>
                  {canDeleteLeadRecord(permissions) && (
                    <Popconfirm
                      title="حذف فایل"
                      description="آیا از حذف این فایل اطمینان دارید؟"
                      onConfirm={() => handleDelete(file.id)}
                      okText="بله، حذف کن"
                      cancelText="لغو"
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title="حذف">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          className="hover:bg-red-50 dark:hover:bg-red-900/20"
                          style={{ fontFamily: 'CustomFont' }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={
          previewFile ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 truncate max-w-[60%]" style={{ fontFamily: 'CustomFont' }}>
                {previewFile.filename || previewFile.name || 'فایل بدون نام'}
              </span>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(previewFile)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  دانلود
                </Button>
                <Button
                  onClick={handleClosePreview}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  بستن
                </Button>
              </Space>
            </div>
          ) : null
        }
        title={
          <div className="flex items-center gap-2">
            <EyeOutlined className="text-blue-500" />
            <span style={{ fontFamily: 'CustomFont' }}>پیش‌نمایش فایل</span>
          </div>
        }
        width={800}
        style={{ top: 20, fontFamily: 'CustomFont' }}
        destroyOnClose
        bodyStyle={{ padding: '16px', minHeight: 200 }}
      >
        {renderPreviewContent()}
      </Modal>
    </div>
  );
};

export default FilesTab;
